<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoney, invoiceStatusClass, supplierInvoiceStatusLabel } from '../utils/format'
import type {
  CurrencyOption,
  Option,
  SupplierInvoice,
  SupplierInvoiceStatus,
} from '../types/finance'

const { t } = useI18n()
const auth = useAuthStore()
const {
  listSupplierInvoices,
  listCurrencies,
  listAllAgentsByRole,
  voidSupplierInvoice,
  deleteSupplierInvoice,
} = useFinanceApi()

const PER_PAGE = 20

const statuses: SupplierInvoiceStatus[] = ['open', 'partial', 'closed', 'void']

const permissions = computed(() => auth.user?.permissions ?? [])
const canCreate = computed(() => permissions.value.includes('supplier_invoices.create'))
const canUpdate = computed(() => permissions.value.includes('supplier_invoices.update'))
const canDelete = computed(() => permissions.value.includes('supplier_invoices.delete'))

const rows = ref<SupplierInvoice[]>([])
const currencies = ref<CurrencyOption[]>([])
const suppliers = ref<Option[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const busyId = ref('')
const page = ref(1)

const filters = ref({
  supplier_agent_id: '' as string | number,
  status: '' as SupplierInvoiceStatus | '',
  currency_code: '',
  search: '',
})

const visibleRows = computed(() => {
  const term = filters.value.search.trim().toLowerCase()
  if (!term) return rows.value
  return rows.value.filter((invoice) =>
    [invoice.invoice_number, invoice.supplier_name, String(invoice.id), invoice.notes]
      .filter((value) => value !== null && value !== undefined)
      .some((value) => String(value).toLowerCase().includes(term)),
  )
})

const totalPages = computed(() => Math.max(1, Math.ceil(visibleRows.value.length / PER_PAGE)))
const pagedRows = computed(() =>
  visibleRows.value.slice((page.value - 1) * PER_PAGE, page.value * PER_PAGE),
)

const rangeLabel = computed(() => {
  const total = visibleRows.value.length
  if (!total) return ''
  const from = (page.value - 1) * PER_PAGE + 1
  const to = Math.min(page.value * PER_PAGE, total)
  return t('finance.common.showing', { from, to, total })
})

const openTotal = computed(() =>
  visibleRows.value
    .filter((invoice) => invoice.status === 'open' || invoice.status === 'partial')
    .reduce((sum, invoice) => sum + Number(invoice.invoice_amount ?? 0), 0),
)

async function load() {
  loading.value = true
  error.value = ''
  try {
    rows.value = await listSupplierInvoices({
      supplier_agent_id: filters.value.supplier_agent_id,
      status: filters.value.status,
      currency_code: filters.value.currency_code,
    })
    page.value = 1
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadOptions() {
  const [currencyResult, supplierResult] = await Promise.allSettled([
    listCurrencies(),
    listAllAgentsByRole('supplier'),
  ])
  currencies.value = currencyResult.status === 'fulfilled' ? currencyResult.value : []
  suppliers.value = supplierResult.status === 'fulfilled' ? supplierResult.value : []
}

async function voidInvoice(invoice: SupplierInvoice) {
  actionError.value = ''
  actionMessage.value = ''
  if (!window.confirm(t('finance.payables.voidConfirm'))) return
  busyId.value = String(invoice.id)
  try {
    await voidSupplierInvoice(invoice.id)
    actionMessage.value = t('finance.payables.voided')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = ''
  }
}

async function remove(invoice: SupplierInvoice) {
  actionError.value = ''
  actionMessage.value = ''
  if (!window.confirm(t('finance.payables.deleteConfirm'))) return
  busyId.value = String(invoice.id)
  try {
    await deleteSupplierInvoice(invoice.id)
    actionMessage.value = t('finance.common.deleted')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = ''
  }
}

watch(
  () => [filters.value.supplier_agent_id, filters.value.status, filters.value.currency_code],
  () => {
    void load()
  },
)

watch(
  () => filters.value.search,
  () => {
    page.value = 1
  },
)

onMounted(() => {
  void loadOptions()
  void load()
})
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('finance.payables.supplierInvoicesTitle') }}</h2>
  </div>

  <div class="toolbar">
    <select v-model="filters.supplier_agent_id" class="filter-select">
      <option value="">{{ t('finance.payables.supplier') }}</option>
      <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">
        {{ supplier.name }}
      </option>
    </select>
    <select v-model="filters.status" class="filter-select">
      <option value="">{{ t('finance.common.allStatuses') }}</option>
      <option v-for="status in statuses" :key="status" :value="status">
        {{ supplierInvoiceStatusLabel(t, status) }}
      </option>
    </select>
    <select v-model="filters.currency_code" class="filter-select">
      <option value="">{{ t('finance.common.allCurrencies') }}</option>
      <option v-for="currency in currencies" :key="currency.id" :value="currency.code">
        {{ currency.code }}
      </option>
    </select>
    <input
      v-model="filters.search"
      class="search-input"
      type="search"
      :placeholder="t('finance.common.searchPlaceholder')"
    >
    <span class="spacer" />
    <div class="summary">
      <span>{{ t('finance.payables.openTotal') }}: <strong>{{ formatMoney(openTotal) }}</strong></span>
      <span>{{ t('finance.common.rows', { count: visibleRows.length }) }}</span>
    </div>
    <RouterLink v-if="canCreate" class="btn-primary" to="/payables/invoices/new">
      {{ t('finance.payables.newInvoice') }}
    </RouterLink>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <div v-else-if="visibleRows.length === 0" class="state-message">
    <p>{{ t('finance.payables.empty') }}</p>
  </div>

  <template v-else>
    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.payables.invoiceNumber') }}</th>
            <th>{{ t('finance.payables.supplier') }}</th>
            <th>{{ t('finance.payables.invoiceDate') }}</th>
            <th>{{ t('finance.common.dueDate') }}</th>
            <th>{{ t('finance.payables.purchaseOrder') }}</th>
            <th>{{ t('finance.payables.warehouse') }}</th>
            <th class="cell-num">{{ t('finance.common.amount') }}</th>
            <th>{{ t('finance.common.status') }}</th>
            <th class="cell-actions">{{ t('finance.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="invoice in pagedRows" :key="invoice.id">
            <td class="cell-code">{{ invoice.invoice_number }}</td>
            <td class="cell-name">{{ invoice.supplier_name || invoice.supplier_agent_id }}</td>
            <td class="cell-date">{{ formatDisplayDate(invoice.invoice_date) }}</td>
            <td class="cell-date">{{ formatDisplayDate(invoice.due_date) }}</td>
            <td>{{ invoice.purchase_order_id ? `#${invoice.purchase_order_id}` : '—' }}</td>
            <td>{{ invoice.warehouse_name || '—' }}</td>
            <td class="cell-num">
              {{ formatMoney(invoice.invoice_amount, invoice.currency_code) }}
            </td>
            <td>
              <span class="status-badge" :class="invoiceStatusClass(invoice.status)">
                {{ supplierInvoiceStatusLabel(t, invoice.status) }}
              </span>
            </td>
            <td class="cell-actions">
              <RouterLink class="link-action" :to="`/payables/invoices/${invoice.id}`">
                {{ t('finance.common.open') }}
              </RouterLink>
              <RouterLink
                v-if="canUpdate && invoice.status !== 'void'"
                class="link-action"
                :to="`/payables/invoices/${invoice.id}/edit`"
              >
                {{ t('finance.common.edit') }}
              </RouterLink>
              <button
                v-if="canUpdate && invoice.status !== 'void'"
                class="link-action danger"
                :disabled="busyId === String(invoice.id)"
                @click="voidInvoice(invoice)"
              >
                {{ t('finance.payables.void') }}
              </button>
              <button
                v-if="canDelete"
                class="link-action danger"
                :disabled="busyId === String(invoice.id)"
                @click="remove(invoice)"
              >
                {{ t('finance.common.delete') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <span>{{ rangeLabel }}</span>
      <button :disabled="page <= 1" @click="page -= 1">{{ t('finance.common.previous') }}</button>
      <span>{{ t('finance.common.page', { page, total: totalPages }) }}</span>
      <button :disabled="page >= totalPages" @click="page += 1">
        {{ t('finance.common.next') }}
      </button>
    </div>
  </template>
</template>
